/**
 * KochbuchPage - Oeffentliche Ansicht einer geteilten Rezept-Sammlung (kein Login).
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ChefHat, Flame, Beef } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const photoSrc = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}/api/portal/nutrition/uploads/${String(u).replace('/uploads/', '')}` : null);

const KochbuchPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/portal/nutrition/collections/shared/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-950"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  if (error || !data) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-gray-400">Sammlung nicht gefunden oder nicht mehr geteilt.</div>;

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="kochbuch-page">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-amber-500 mb-2"><ChefHat className="w-6 h-6" /><span className="text-sm uppercase tracking-wide">Kochbuch</span></div>
        <h1 className="text-3xl font-bold">{data.name}</h1>
        {data.description && <p className="text-gray-400 mt-1">{data.description}</p>}
        <p className="text-sm text-gray-500 mt-1">von {data.author_name} · {data.recipe_count} Rezept(e)</p>

        <div className="mt-8 space-y-4">
          {(data.recipes || []).map((r) => (
            <div key={r.id} className="bg-white/5 rounded-xl overflow-hidden border border-white/10">
              {r.photo_url && <img src={photoSrc(r.photo_url)} alt="" className="w-full h-44 object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
              <div className="p-4">
                <h3 className="font-semibold text-lg">{r.name}</h3>
                {r.category && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">{r.category}</span>}
                <div className="flex gap-3 mt-2 text-sm text-gray-400">
                  <span className="flex items-center gap-1"><Flame className="w-4 h-4" />{Math.round(r.total_calories || r.totals?.calories || 0)} kcal</span>
                  <span className="flex items-center gap-1"><Beef className="w-4 h-4" />{Math.round(r.total_protein || r.totals?.protein || 0)} g</span>
                </div>
                {r.ingredients?.length > 0 && (
                  <ul className="mt-3 text-sm text-gray-300 space-y-0.5">
                    {r.ingredients.map((i, idx) => <li key={idx} className="flex justify-between border-b border-white/5 py-0.5"><span>{i.name}</span><span className="text-gray-500">{i.amount_g || i.amount || 0} {i.unit || 'g'}</span></li>)}
                  </ul>
                )}
                {r.steps?.length > 0 && (
                  <ol className="mt-3 text-sm text-gray-300 space-y-1 list-decimal list-inside">
                    {r.steps.map((s, idx) => <li key={idx}>{s}</li>)}
                  </ol>
                )}
              </div>
            </div>
          ))}
          {(!data.recipes || data.recipes.length === 0) && <p className="text-gray-500">Diese Sammlung enthält noch keine Rezepte.</p>}
        </div>
      </div>
    </div>
  );
};

export default KochbuchPage;
